import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  IconButton,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Select as ChakraSelect,
  Textarea,
  useToast,
  useColorModeValue,
  Switch,
  Alert,
  AlertIcon,
  Icon,
  Center,
  Spinner,
  Code,
} from '@chakra-ui/react';
import {
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiSettings,
} from 'react-icons/fi';
import { Select } from 'chakra-react-select';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { app } from '../../../utils/init-firebase';
import { useCrmSettings } from '../../../contexts/CrmSettingsContext';
import { CUSTOM_FIELD_TYPES } from '../../../constants/crmConstants';

/**
 * Custom Fields Manager
 * 
 * Allows admins to add dynamic custom fields to contacts.
 * Custom fields are rendered in the ContactDetailDrawer edit form.
 * 
 * Field configuration:
 * - Field name (camelCase identifier)
 * - Display label
 * - Field type (text, number, select, date, boolean, etc.)
 * - Options (for select fields)
 * - Required flag
 * - Applies to which contact types
 * - Validation rules
 */
export default function CustomFieldsManager() {
  const toast = useToast();
  const { customContactFields, contactTypes, loading: settingsLoading } = useCrmSettings();
  const [selectedField, setSelectedField] = useState(null);

  const { isOpen, onOpen, onClose } = useDisclosure();

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const tableHeaderBG = useColorModeValue('gray.50', 'gray.800');
  const hoverBG = useColorModeValue('gray.50', 'gray.700');

  const handleAdd = () => {
    setSelectedField(null);
    onOpen();
  };

  const handleEdit = (field) => {
    setSelectedField(field);
    onOpen();
  };

  const handleDelete = async (field) => {
    if (!window.confirm(`Are you sure you want to delete the custom field "${field.label}"? This will remove the field from all contacts.`)) {
      return;
    }

    try {
      const functions = getFunctions(app, 'us-east1');
      const updateCrmSettings = httpsCallable(functions, 'updateCrmSettings');

      const updatedFields = customContactFields.filter(f => f.fieldName !== field.fieldName);

      const result = await updateCrmSettings({
        updates: {
          customContactFields: updatedFields,
        },
      });

      if (result.data.success) {
        toast({
          title: 'Custom field deleted',
          status: 'success',
          duration: 2000,
        });
      }
    } catch (error) {
      console.error('Error deleting field:', error);
      toast({
        title: 'Error',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    }
  };

  if (settingsLoading) {
    return (
      <Center py={12}>
        <Spinner size="lg" />
      </Center>
    );
  }

  return (
    <Box>
      <VStack align="stretch" spacing={6}>
        <HStack justify="space-between">
          <VStack align="flex-start" spacing={0}>
            <Heading size="md">Custom Contact Fields</Heading>
            <Text fontSize="sm" color="gray.600">
              Add dynamic fields to contacts for your specific workflow
            </Text>
          </VStack>
          <Button
            leftIcon={<FiPlus />}
            colorScheme="blue"
            onClick={handleAdd}
          >
            Add Custom Field
          </Button>
        </HStack>

        <Alert status="info" borderRadius="md">
          <AlertIcon />
          <VStack align="flex-start" spacing={1} flex={1}>
            <Text fontSize="sm" fontWeight="semibold">
              About Custom Fields
            </Text>
            <Text fontSize="xs">
              Custom fields appear in the contact edit form. You can restrict fields to specific 
              contact types (e.g., "License Renewal Date" only for agents).
            </Text>
          </VStack>
        </Alert>

        {customContactFields && customContactFields.length === 0 ? (
          <Center py={12}>
            <VStack>
              <Icon as={FiSettings} boxSize={12} color="gray.300" />
              <Text color="gray.500" fontSize="sm">
                No custom fields yet
              </Text>
              <Button
                size="sm"
                leftIcon={<FiPlus />}
                colorScheme="blue"
                variant="outline"
                mt={2}
                onClick={handleAdd}
              >
                Create Your First Field
              </Button>
            </VStack>
          </Center>
        ) : (
          <Box overflowX="auto">
            <Table variant="simple" size="sm">
              <Thead bg={tableHeaderBG}>
                <Tr>
                  <Th>Field Name</Th>
                  <Th>Label</Th>
                  <Th>Type</Th>
                  <Th>Required</Th>
                  <Th>Applies To</Th>
                  <Th>Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {customContactFields.map((field) => (
                  <Tr key={field.fieldName} _hover={{ bg: hoverBG }}>
                    <Td fontFamily="mono" fontSize="xs">
                      <Code fontSize="xs">{field.fieldName}</Code>
                    </Td>
                    <Td fontWeight="medium">{field.label}</Td>
                    <Td>
                      <Badge colorScheme="purple">
                        {CUSTOM_FIELD_TYPES.find(t => t.value === field.fieldType)?.label || field.fieldType}
                      </Badge>
                    </Td>
                    <Td>
                      <Badge colorScheme={field.required ? 'red' : 'gray'}>
                        {field.required ? 'Yes' : 'No'}
                      </Badge>
                    </Td>
                    <Td>
                      {field.appliesTo && field.appliesTo.length > 0 ? (
                        field.appliesTo.includes('all') ? (
                          <Badge colorScheme="blue">All Types</Badge>
                        ) : (
                          <HStack spacing={1}>
                            {field.appliesTo.slice(0, 2).map(type => {
                              const typeConfig = contactTypes?.find(t => t.value === type);
                              return (
                                <Badge key={type} size="xs" colorScheme={typeConfig?.color || 'gray'}>
                                  {typeConfig?.label || type}
                                </Badge>
                              );
                            })}
                            {field.appliesTo.length > 2 && (
                              <Badge size="xs" colorScheme="gray">+{field.appliesTo.length - 2}</Badge>
                            )}
                          </HStack>
                        )
                      ) : (
                        <Badge colorScheme="blue">All Types</Badge>
                      )}
                    </Td>
                    <Td>
                      <HStack spacing={1}>
                        <IconButton
                          icon={<FiEdit2 />}
                          size="xs"
                          variant="ghost"
                          aria-label="Edit"
                          onClick={() => handleEdit(field)}
                        />
                        <IconButton
                          icon={<FiTrash2 />}
                          size="xs"
                          variant="ghost"
                          colorScheme="red"
                          aria-label="Delete"
                          onClick={() => handleDelete(field)}
                        />
                      </HStack>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        )}
      </VStack>

      {/* Field Editor Modal */}
      <FieldEditorModal
        isOpen={isOpen}
        onClose={onClose}
        field={selectedField}
        existingFields={customContactFields}
        contactTypes={contactTypes}
      />
    </Box>
  );
}

function FieldEditorModal({ isOpen, onClose, field, existingFields, contactTypes }) {
  const toast = useToast();
  const [fieldName, setFieldName] = useState('');
  const [label, setLabel] = useState('');
  const [fieldType, setFieldType] = useState('text');
  const [required, setRequired] = useState(false);
  const [options, setOptions] = useState('');
  const [appliesToTypes, setAppliesToTypes] = useState([{ value: 'all', label: 'All Contact Types' }]);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (field) {
      setFieldName(field.fieldName || '');
      setLabel(field.label || '');
      setFieldType(field.fieldType || 'text');
      setRequired(field.required || false);
      setOptions((field.options || []).join('\n'));
      
      if (field.appliesTo && !field.appliesTo.includes('all')) {
        setAppliesToTypes(
          field.appliesTo.map(val => ({
            value: val,
            label: contactTypes?.find(t => t.value === val)?.label || val,
          }))
        );
      } else {
        setAppliesToTypes([{ value: 'all', label: 'All Contact Types' }]);
      }
    } else {
      setFieldName('');
      setLabel('');
      setFieldType('text');
      setRequired(false);
      setOptions('');
      setAppliesToTypes([{ value: 'all', label: 'All Contact Types' }]);
    }
  }, [field, contactTypes, isOpen]);

  const handleSave = async () => {
    if (!fieldName.trim() || !label.trim()) {
      toast({
        title: 'Missing fields',
        description: 'Field name and label are required',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    // Validate field name format (camelCase)
    if (!/^[a-z][a-zA-Z0-9]*$/.test(fieldName)) {
      toast({
        title: 'Invalid field name',
        description: 'Field name must be camelCase (start lowercase, no spaces)',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    // Validate select fields have options
    if ((fieldType === 'select' || fieldType === 'multiselect') && !options.trim()) {
      toast({
        title: 'Options required',
        description: 'Select fields must have options (one per line)',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    setIsSaving(true);
    try {
      const functions = getFunctions(app, 'us-east1');
      const updateCrmSettings = httpsCallable(functions, 'updateCrmSettings');

      let updatedFields = [...(existingFields || [])];

      const newField = {
        fieldName: fieldName.trim(),
        label: label.trim(),
        fieldType,
        required,
        appliesTo: appliesToTypes.map(t => t.value),
        visibleToRoles: ['all'],
      };

      if (fieldType === 'select' || fieldType === 'multiselect') {
        newField.options = options.split('\n').map(o => o.trim()).filter(o => o);
      }

      if (field) {
        // Update existing
        const index = updatedFields.findIndex(f => f.fieldName === field.fieldName);
        if (index > -1) {
          updatedFields[index] = newField;
        }
      } else {
        // Add new
        if (updatedFields.some(f => f.fieldName === fieldName.trim())) {
          toast({
            title: 'Duplicate field name',
            description: 'A field with this name already exists',
            status: 'error',
            duration: 3000,
          });
          setIsSaving(false);
          return;
        }
        updatedFields.push(newField);
      }

      const result = await updateCrmSettings({
        updates: {
          customContactFields: updatedFields,
        },
      });

      if (result.data.success) {
        toast({
          title: field ? 'Updated' : 'Created',
          description: `Custom field ${field ? 'updated' : 'created'} successfully`,
          status: 'success',
          duration: 2000,
        });
        onClose();
      }
    } catch (error) {
      console.error('Error saving field:', error);
      toast({
        title: 'Error',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const needsOptions = fieldType === 'select' || fieldType === 'multiselect';

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {field ? 'Edit' : 'Create'} Custom Field
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <FormControl isRequired>
              <FormLabel fontSize="sm">Field Name (Unique ID)</FormLabel>
              <Input
                value={fieldName}
                onChange={(e) => setFieldName(e.target.value.replace(/[^a-zA-Z0-9]/g, ''))}
                placeholder="e.g., preferredContactTime"
                isDisabled={!!field}
              />
              <Text fontSize="xs" color="gray.600" mt={1}>
                camelCase, no spaces. Cannot be changed after creation. Used in data storage.
              </Text>
            </FormControl>

            <FormControl isRequired>
              <FormLabel fontSize="sm">Display Label</FormLabel>
              <Input
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="e.g., Preferred Contact Time"
              />
              <Text fontSize="xs" color="gray.600" mt={1}>
                User-friendly name shown in forms
              </Text>
            </FormControl>

            <FormControl isRequired>
              <FormLabel fontSize="sm">Field Type</FormLabel>
              <ChakraSelect
                value={fieldType}
                onChange={(e) => setFieldType(e.target.value)}
              >
                {CUSTOM_FIELD_TYPES.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </ChakraSelect>
            </FormControl>

            {needsOptions && (
              <FormControl isRequired>
                <FormLabel fontSize="sm">Options (One Per Line)</FormLabel>
                <Textarea
                  value={options}
                  onChange={(e) => setOptions(e.target.value)}
                  placeholder="Option 1&#10;Option 2&#10;Option 3"
                  rows={5}
                  fontFamily="mono"
                  fontSize="sm"
                />
                <Text fontSize="xs" color="gray.600" mt={1}>
                  Each line will be a selectable option
                </Text>
              </FormControl>
            )}

            <FormControl>
              <FormLabel fontSize="sm">Applies To Contact Types</FormLabel>
              <Select
                isMulti
                value={appliesToTypes}
                onChange={setAppliesToTypes}
                options={[
                  { value: 'all', label: 'All Contact Types' },
                  ...(contactTypes?.map(type => ({
                    value: type.value,
                    label: type.label,
                  })) || [])
                ]}
                placeholder="Select contact types..."
              />
              <Text fontSize="xs" color="gray.600" mt={1}>
                Field will only appear for these contact types. Select "All" for universal fields.
              </Text>
            </FormControl>

            <FormControl display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <FormLabel fontSize="sm" mb={0}>Required Field</FormLabel>
                <Text fontSize="xs" color="gray.600">
                  Must be filled when creating/editing contacts
                </Text>
              </Box>
              <Switch
                isChecked={required}
                onChange={(e) => setRequired(e.target.checked)}
                colorScheme="red"
                size="lg"
              />
            </FormControl>

            <Alert status="warning" size="sm">
              <AlertIcon />
              <Text fontSize="xs">
                Deleting a custom field will remove it from all contacts. The data will be lost.
              </Text>
            </Alert>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleSave}
            isLoading={isSaving}
            loadingText="Saving..."
          >
            {field ? 'Update' : 'Create'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

